import logging from "utils/logging";

const log = logging.getLogger("renderCardToImage");

/**
 * Render a DOM node to a PNG blob, then prompt the user to share or download.
 *
 * @param {HTMLElement} node - element to render (typically a .share-card)
 * @param {string} filename - suggested filename for the download/share file
 * @param {(kind: string, extra?: object) => void} [onShareEvent] - optional callback
 *   invoked at share lifecycle points: "initiated", "completed", "dismissed",
 *   "failed", "downloaded"
 * @returns {Promise<void>}
 */
export const shareCardImage = async (node, filename = "groupardy.png", onShareEvent) => {
  const emit = (kind, extra = {}) => onShareEvent && onShareEvent(kind, extra);

  if (!node) {
    log.warn("shareCardImage called with null node");
    emit("failed", { errorName: "NullNode" });
    return;
  }

  let blob;
  try {
    // Lazy-import html-to-image — keeps it out of the initial bundle.
    // html-to-image uses <foreignObject> SVG, so the browser does text
    // layout — spaces and punctuation render faithfully even with custom
    // display fonts (html2canvas's measureText mangled both).
    const { toBlob } = await import("html-to-image");

    blob = await toBlob(node, {
      pixelRatio: 2.5,
      cacheBust: true,
    });
    if (!blob) {
      log.error("toBlob produced null");
      emit("failed", { errorName: "BlobCreationFailed" });
      return;
    }
  } catch (err) {
    emit("failed", { errorName: err?.name || "RenderError" });
    return;
  }

  const file = new File([blob], filename, { type: "image/png" });
  const canNative = navigator.canShare && navigator.canShare({ files: [file] });

  emit("initiated", { shareMethod: canNative ? "native_share" : "download" });

  if (canNative) {
    try {
      await navigator.share({ files: [file], title: "Groupardy" });
      emit("completed", { shareMethod: "native_share" });
      return;
    } catch (err) {
      if (err?.name === "AbortError") {
        emit("dismissed", { shareMethod: "native_share" });
      } else {
        log.debug("share failed:", err?.name);
        emit("failed", { shareMethod: "native_share", errorName: err?.name || "ShareError" });
      }
      return;
    }
  }

  // Desktop fallback: trigger download
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    emit("downloaded", { shareMethod: "download" });
  } catch (err) {
    emit("failed", { shareMethod: "download", errorName: err?.name || "DownloadError" });
  }
};
